<script>
import { mapGetters } from 'vuex'
import CalendarResource from '@/resources/calendar'
import ReservationResource from '@/resources/reservation'
import Checkout from '@/components/Checkout.vue'
import CalendarReservationsSimple from '../components/CalendarReservationsSimple.vue'
import messages from '@/mixins/messages'
import moment from 'moment'
import { TIMEZONE } from '@/config'
import ResourceAttribute from '../components/ResourceAttribute.vue'
import { timeDateFormattedPretty, dateFormattedPretty } from '../util'
import WaiverModal from '../components/WaiverModal.vue'

export default {
    name: 'ReservationsCreate',
    mixins: [messages],
    components: {
        Checkout,
        CalendarReservationsSimple,
        ResourceAttribute,
        WaiverModal
    },
    computed: {
        ...mapGetters(['userResource']),
        bookDateFormattedPretty() {
            return moment(this.bookDate).format('dddd, MMMM Do')
        },
        bookDateFormatted() {
            return moment(this.bookDate).format('YYYY-MM-DD')
        },
        bookTimeDateFormattedPretty() {
            if(!this.bookTimeSlotResource) {
                return this.bookDateFormattedPretty
            }
            return moment(this.bookTimeSlotResource.dateTime).format('dddd, MMMM Do \@ h:mm a')
        },
        canBookWithSelectedCar() {
            return this.bookTimeSlotResource && this.bookSubscriptionResource && parseInt(this.bookSubscriptionResource.get('reservations_remaining')) > 0
        },
        reservationResourcesAvailable() {
            let now = new Date()
            return this.reservationResources.filter(resource => {
                let dt = resource.dateTime
                return dt > now && ~[0, 30].indexOf(dt.getMinutes())
            })
        },
        calendarWrapperClasses() {
            if (this.calendarForceVisible) {
                return 'd-block d-lg-none'
            } else {
                return 'd-none d-lg-block'
            }
        }
    },
    data() {
        const now = new Date()
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        const minDate = new Date(now.getFullYear(), now.getMonth() - 2, 0)
        const maxDate = new Date(now.getFullYear(), now.getMonth() + 2, 0)
        return {
            calendarResource: null,
            calendarResourceImageURL: null,
            calendarForceVisible: false,
            reservationResources: [],
            loading: true,
            loadingReservationTimes: false,
            bookDate: today,
            bookTimeSlotResource: null,
            bookSubscriptionResource: null,
            dateMin: minDate,
            dateMax: maxDate,
            tz: TIMEZONE,
            bookModalSize: 'lg',
        }
    },
    methods: {
        timeDateFormattedPretty,
        dateFormattedPretty,
        async bookDateChange() {
            this.bookTimeSlotResource = null
        },
        forceCalendarOpen() {
            this.calendarForceVisible = true
            window.scroll(0, 0)
        },
        async onBookingCreate(commitData) {
            this.$refs.bookModal.hide()
            this.$analytics.track('created_reservation')

            if (this.$refs.waiverModal.hasOutstanding()) {
                this.$refs.waiverModal.next()
            } else if (commitData.redirect) {
                window.location.href = commitData.redirect
            } else {
                this.$router.push({ name: 'calendarControlWaitingRoom', params: { calendarId: this.calendarResource.id } })
            }
        },
        onBookingError() {

        },
        onCalendarSelect(calendar, reservations) {
            this.reservationResources = reservations
        },
        onCheckout() {
            this.$refs.bookModal.show()
        }
    },
    async mounted() {
        this.loading = true

        let [userReservationResources, calendarResource] = await Promise.all([
            ReservationResource.filterAll({ user: this.userResource.id, group_base: true, future: true }),
            CalendarResource.detail(this.$route.params.calendarId),
        ])

        this.userReservationResources = userReservationResources
        this.calendarResource = calendarResource
        this.calendarResourceImageURL = await this.calendarResource.resolveAttribute('image.url')
        this.loading = false

        this.userReservationResources.forEach(resource => resource.resolveRelated('subscription'))

        this.$nextTick(() => {
            this.$refs.calendar.$emit('input', this.bookDate)
            addeventatc.refresh()
        })
    },
}
</script>

<template>
    <div>
        <header-image kicker="Book a Simulator">
            <h1>Reservations</h1>
        </header-image>

        <b-container class="mt-3" fluid="lg" v-if="!loading">
            <b-alert v-if="!calendarForceVisible" show variant="info" class="d-block d-lg-none">
                <strong>Selected Date:</strong> {{ dateFormattedPretty(bookDate) }} (<a href="javascript:{}" @click="forceCalendarOpen()">change</a>)
                <b-link :to="{ name: 'reservationsAdvanced', query: { location: calendarResource.attributes.location } }">view other simulators</b-link>
            </b-alert>
            
            <b-row>
                <b-col md="4" offset-md="2" class="mt-2">
                    <b-card bg-variant="light" :class="calendarWrapperClasses">
                        <b-calendar v-model="bookDate" block value-as-date :hide-header="true" @input="bookDateChange" ref="calendar" selected-variant="success" today-variant="info" locale="en" :min="dateMin" :max="dateMax"></b-calendar>
                        <hr />
                        <b-button variant="primary" @click="onCheckout()" :disabled="reservationResources.length < 1" class="col-sm-12 mb-2">Checkout</b-button>
                    </b-card>
                </b-col>

                <b-col sm="4" class="mt-2 mb-5">
                    <div v-if="messages.length > 0">
                        <b-alert :variant="message.variant" show v-for="(message, i) in messages" :key="i">{{ message.text }}</b-alert>
                    </div>

                    <b-card :img-src="calendarResourceImageURL" img-top>
                        <calendar-reservations-simple
                            :calendar-id="calendarResource.id"
                            :for-date="bookDate"
                            @input="(reservations) => onCalendarSelect(calendarResource, reservations)"
                            many>
                            <template slot="calendar-body" slot-scope="data">
                                <b-button variant="primary" size="sm" block @click="forceCalendarOpen()" v-if="data.reservations.length == 0" class="d-block d-lg-none">Change Selected Date</b-button>
                            </template>
                        </calendar-reservations-simple>
                    </b-card>

                    <b-button :to="{ name: 'reservationsAdvanced', query: { location: calendarResource.attributes.location } }" variant="primary" class="col-md-12 mt-3 mb-5">View other simulators</b-button>
                </b-col>
            </b-row>
        </b-container>
        
        <b-container class="mt-3 d-block d-lg-none pt-3" id="taco" v-if="reservationResources.length > 0"  fluid>
            <b-row>
                <b-col class="text-center text-muted">
                    <b-button variant="primary" @click="onCheckout()" class="col-sm-12">Checkout</b-button>
                    <small>Selected {{ (reservationResources.length === 1) ? '1 Timeslot' : `${reservationResources.length} Timeslots` }} with 1 Simulator</small>
                </b-col>
            </b-row>
        </b-container>

        <b-modal ref="bookModal" :size="bookModalSize" title="Checkout" hide-footer no-fade centered>
            <checkout
                :reservation-resources="reservationResources"
                @size="(newSize) => bookModalSize = newSize"
                @success="(commitData) => onBookingCreate(commitData)"
                @error="onBookingError"
                use-payment-link>
            </checkout>
        </b-modal>

        <b-modal ref="completeModal" title="All Done!" ok-title="Log Out" cancel-title="Book Another" cancel-variant="primary" no-fade no-close-on-backdrop no-close-on-esc hide-header-close centered>
            <h3>Simulator Successfully Booked</h3>
            <p>What would you like to do next?</p>
        </b-modal>

        <b-modal ref="errorModal" title="Booking Error" ok-title="Review Payment Method" cancel-title="Go Back" cancel-variant="primary" no-fade no-close-on-backdrop no-close-on-esc hide-header-close centered>
            <h3>Booking encountered an error</h3>
            <b-alert variant="danger" show>Your reservation could not be completed, there were issues processing your payment.</b-alert>
        </b-modal>

        <waiver-modal ref="waiverModal" @success="onBookingCreate" />
    </div>
</template>

<style lang="scss">
#taco {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    z-index: 999;
    background-color: var(--panel);
    color: var(--ink);
    border-top: 1px solid var(--line-2);
    box-shadow: 0 -10px 30px rgba(0, 0, 0, 0.28);
    padding-bottom: 1rem;
}
:root[data-theme='dark'] #taco {
    background-color: var(--panel-2);
    border-top-color: var(--line-2);
    box-shadow: 0 -10px 30px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.05);
}
</style>
